import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TrustIndicators = () => {
  const testimonials = [
    { name: 'Jan Bakker', role: 'Projectleider', company: 'Bakker Bouw BV', content: 'Via BVM Group vonden we binnen 24 uur de perfecte vakmensen voor ons zonnepanelenproject. De kwaliteit en service zijn uitstekend.', rating: 5 },
    { name: 'Maria van Dijk', role: 'Vakman', company: 'Zelfstandig Specialist', content: 'Als ZZP\'er krijg ik nu veel meer warmtepomp en zonnepaneel opdrachten binnen. Het platform is gebruiksvriendelijk en eerlijk.', rating: 5 },
    { name: 'Peter Jansen', role: 'Partner', company: 'TechRecruit NL', content: 'Het partner programma werkt perfect. Real-time inzicht in commissies en een professioneel dashboard maken het verschil.', rating: 5 }
  ];

  const stats = [
    { value: '8.500+', label: 'Projecten ingevuld', icon: 'CheckCircle' },
    { value: '1.800+', label: 'Gecertificeerde vakmensen', icon: 'Users' },
    { value: '98%', label: 'Klanttevredenheid', icon: 'ThumbsUp' },
    { value: '24/7', label: 'Platform beschikbaar', icon: 'Clock' }
  ];

  return (
    <section id="trust" className="py-20 md:py-28 bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-[140px] opacity-5" style={{ background: '#00E5A0' }} />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          className="mb-14"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.p className="text-xs font-body font-medium tracking-[0.2em] uppercase mb-4" style={{ color: '#00E5A0' }}>
            — Vertrouwen
          </motion.p>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
            Vertrouwd door professionals
          </h2>
          <p className="text-base max-w-xl" style={{ color: '#888888' }}>
            Ontdek waarom duizenden bedrijven en vakmensen kiezen voor BVM Group
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mb-16" style={{ background: '#1A1A1A' }}>
          {stats?.map((stat, index) => (
            <motion.div
              key={index}
              className="p-8 text-center"
              style={{ background: '#0A0A0A' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="w-10 h-10 rounded flex items-center justify-center mx-auto mb-4" style={{ border: '1px solid #2A2A2A' }}>
                <Icon name={stat?.icon} size={20} color="#00E5A0" />
              </div>
              <p className="text-3xl font-heading font-bold text-white mb-1">{stat?.value}</p>
              <p className="text-xs font-body uppercase tracking-wide" style={{ color: '#555555' }}>{stat?.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: '#1A1A1A' }}>
          {testimonials?.map((testimonial, index) => (
            <motion.div
              key={index}
              className="p-8"
              style={{ background: '#0A0A0A' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ background: '#0F0F0F' }}
            >
              <div className="flex items-center gap-1 mb-5">
                {[...Array(testimonial?.rating)]?.map((_, i) => (
                  <Icon key={i} name="Star" size={14} color="#00E5A0" />
                ))}
              </div>
              <p className="text-sm leading-relaxed mb-6 italic" style={{ color: '#888888' }}>
                "{testimonial?.content}"
              </p>
              <div className="flex items-center gap-3 pt-5 border-t" style={{ borderColor: '#1A1A1A' }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#1A1A1A' }}>
                  <Icon name="User" size={16} color="#00E5A0" />
                </div>
                <div>
                  <p className="text-sm font-body font-semibold text-white">{testimonial?.name}</p>
                  <p className="text-xs" style={{ color: '#555555' }}>{testimonial?.role} · {testimonial?.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust badge */}
        <motion.div
          className="mt-px p-8 flex flex-col md:flex-row items-center gap-6"
          style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', marginTop: '1px' }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0" style={{ border: '1px solid #2A2A2A' }}>
            <Icon name="Shield" size={24} color="#00E5A0" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-heading font-semibold text-lg text-white mb-1">Kwaliteit en veiligheid gegarandeerd</h3>
            <p className="text-sm" style={{ color: '#666666' }}>Alle vakmensen worden gescreend, het platform is GDPR-compliant en betalingen zijn beveiligd</p>
          </div>
          <div className="flex items-center gap-6">
            {[{ icon: 'CheckCircle', label: 'Gescreend' }, { icon: 'Lock', label: 'Beveiligd' }, { icon: 'Award', label: 'Gecertificeerd' }]?.map(b => (
              <div key={b?.label} className="text-center">
                <Icon name={b?.icon} size={22} color="#00E5A0" className="mx-auto mb-1" />
                <p className="text-xs" style={{ color: '#555555' }}>{b?.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustIndicators;